/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Apply the "MMFF-lite" CHON parameter packs to a molecule's radial and
 * angular bonds in place — the bond/angle analogue of applyTorsionParameters
 * (torsionParamLookup.ts), and the single entry point for Regime A of the
 * default-force-field accuracy upgrade.
 *
 * Pipeline:
 *   1. Perceive a coarse atom type for every atom (mmffLiteTyping).
 *   2. For each radial bond, set its stiffness from the typed-pair table; if
 *      the pair isn't in the pack, leave RadialBond.DEFAULT_STRENGTH untouched.
 *   3. For each angular bond, do the same with the angle table.
 *
 * The fallback is what makes this safe to drop in: anything the pack doesn't
 * cover behaves exactly as it does today (global k = 10), so the change is
 * never worse than the status quo and degrades gracefully on exotic chemistry.
 * Only the force CONSTANTS are touched — equilibrium length/angle stay at the
 * as-built geometry (Regime A), so no rest energy is introduced.
 *
 * Call this once after a molecule's topology is built (the same sites that
 * construct RadialBond / AngularBond objects — note there are two such paths,
 * the bond-graph builder and the dynamics viewer's override, so both need the
 * call). Bond ORDER must be populated on rb.type for the typer to see π bonds;
 * the dynamics path currently drops chem-lib bond order, so wiring this in
 * production also means propagating order → rb.type at construction.
 */

import { Atom } from './Atom.ts';
import { ImproperBond } from './ImproperBond.ts';
import { perceiveAtomTypes, bondOrderOf, TypingBond } from './mmffLiteTyping.ts';
import { lookupBondStrength } from './mmffBondParameters.ts';
import { lookupAngleStrength } from './mmffAngleParameters.ts';
import { lookupTorsionStiffness, genericTorsion } from './mmffTorsionParameters.ts';
import { lookupImproperK, HYDROGEN_IMPROPER_K } from './mmffImproperParameters.ts';

// Structural views — RadialBond / AngularBond satisfy these without importing
// the concrete classes (keeps the lookup decoupled and unit-testable).
export interface RadialBondLike extends TypingBond {
  strength: number;
}
export interface AngularBondLike {
  atom1: Atom; // terminal
  atom2: Atom; // central
  atom3: Atom; // terminal
  strength: number;
}

export interface ApplyMmffResult {
  types: Map<Atom, string>;
  bondsSet: number; // how many radial bonds got a pack value
  anglesSet: number; // how many angular bonds got a pack value
  bondsTotal: number;
  anglesTotal: number;
}

/**
 * Assign MMFF-lite force constants to the given bonds/angles. `atoms` must be
 * the full atom list so the typer sees complete connectivity. `angularBonds`
 * is optional — pass it to parameterize angles too.
 */
export const applyMmffParameters = (
  atoms: Atom[],
  radialBonds: RadialBondLike[],
  angularBonds?: AngularBondLike[],
): ApplyMmffResult => {
  const types = perceiveAtomTypes(atoms, radialBonds);
  let bondsSet = 0;
  let anglesSet = 0;

  for (const rb of radialBonds) {
    const ta = types.get(rb.atom1);
    const tb = types.get(rb.atom2);
    if (ta === undefined || tb === undefined) continue;
    const kk = lookupBondStrength(ta, tb, bondOrderOf(rb.type));
    if (kk !== null) {
      rb.strength = kk;
      bondsSet++;
    }
  }

  if (angularBonds) {
    for (const ab of angularBonds) {
      const ta = types.get(ab.atom1);
      const tc = types.get(ab.atom2); // center
      const te = types.get(ab.atom3);
      if (ta === undefined || tc === undefined || te === undefined) continue;
      const kk = lookupAngleStrength(tc, ta, te);
      if (kk !== null) {
        ab.strength = kk;
        anglesSet++;
      }
    }
  }

  return {
    types,
    bondsSet,
    anglesSet,
    bondsTotal: radialBonds.length,
    anglesTotal: angularBonds?.length ?? 0,
  };
};

// Structural view of a torsional bond — TorsionalBond satisfies this.
export interface TorsionalBondLike {
  atom1: Atom;
  atom2: Atom; // central
  atom3: Atom; // central
  atom4: Atom;
  strength: number;
  periodicity: number;
  dihedral: number; // phase γ (radians)
  builtDihedral: number; // as-built proper dihedral φ₀ (radians) — the Regime A pin
}

/**
 * Apply the CHON torsion pack (Phase 1, Regime A) to a molecule's torsions in
 * place. For each torsion the central bond (atoms 2-3) determines the height +
 * periodicity (mmffTorsionParameters), and the phase γ is set from the as-built
 * dihedral so a minimum sits at the loaded conformation (V = 0 at rest → no
 * rest-energy injection, no minimize-on-load needed).
 *
 * Torsions whose central atom is sp (type `*.1`, a linear center) have no
 * defined dihedral and are zeroed. Everything else gets either a pack value or
 * the generic order-based fallback — so nothing keeps the over-stiff k=1
 * default that froze conformations.
 *
 * Bond ORDER must be populated on the radial bonds for the central-bond order
 * to be read (same propagation requirement as the bond/angle pack).
 */
export const applyMmffTorsionParameters = (
  atoms: Atom[],
  radialBonds: RadialBondLike[],
  torsions: TorsionalBondLike[],
): { types: Map<Atom, string>; torsionsSet: number; torsionsTotal: number } => {
  const types = perceiveAtomTypes(atoms, radialBonds);

  // Central-bond order, keyed by atom identity (no index assumptions).
  const orderMap = new Map<Atom, Map<Atom, number>>();
  const addOrder = (a: Atom, b: Atom, o: number) => {
    let inner = orderMap.get(a);
    if (!inner) orderMap.set(a, (inner = new Map()));
    inner.set(b, o);
  };
  for (const rb of radialBonds) {
    const o = bondOrderOf(rb.type);
    addOrder(rb.atom1, rb.atom2, o);
    addOrder(rb.atom2, rb.atom1, o);
  }

  // Multiplicity: how many torsions share each central bond. A pack value is
  // the TOTAL barrier about that bond, so it must be distributed among the
  // torsions that share it — otherwise an sp³–sp³ bond (9 H/C–C–H/C torsions)
  // would be ~9× too stiff and freeze rotation entirely. Keyed by the unordered
  // central-atom index pair.
  const pairKey = (a: Atom, b: Atom) => (a.index < b.index ? `${a.index}-${b.index}` : `${b.index}-${a.index}`);
  const mult = new Map<string, number>();
  for (const t of torsions) {
    const key = pairKey(t.atom2, t.atom3);
    mult.set(key, (mult.get(key) ?? 0) + 1);
  }

  let torsionsSet = 0;
  for (const t of torsions) {
    const tb = types.get(t.atom2);
    const tc = types.get(t.atom3);
    if (tb === undefined || tc === undefined) continue;
    // sp center ⇒ near-linear ⇒ no defined torsion: contribute nothing.
    if (tb.endsWith('.1') || tc.endsWith('.1')) {
      t.strength = 0;
      continue;
    }
    const order = orderMap.get(t.atom2)?.get(t.atom3) ?? 1;
    const params = lookupTorsionStiffness(tb, tc, order) ?? genericTorsion(order);
    if (!params) {
      t.strength = 0; // e.g. triple-bond center
      continue;
    }
    // Regime A: pin a minimum at the AS-BUILT dihedral so V = 0 at rest. Use
    // the construction-time φ₀, not getCurrentDihedral(): the viewer builds
    // torsions from the SDF but places the atoms at the saved (MD-evolved)
    // positions before calling this, so reading the live geometry would move
    // every torsional minimum to the instantaneous conformation on each
    // reload / clone — while bond lengths and angles stay pinned to the SDF.
    const phi = t.builtDihedral;
    t.strength = params.strength / (mult.get(pairKey(t.atom2, t.atom3)) ?? 1);
    t.periodicity = params.periodicity;
    t.dihedral = params.periodicity * phi;
    torsionsSet++;
  }

  return { types, torsionsSet, torsionsTotal: torsions.length };
};

/**
 * Build out-of-plane (improper) terms for every 3-coordinate sp² center
 * (mmffImproperParameters). Returns a fresh ImproperBond[] to assign to the
 * molecule; generated from topology (like angular/torsional bonds) rather than
 * persisted. Centers that aren't sp² 3-coordinate get nothing.
 *
 * Bond order must be populated on the radial bonds so the typer can perceive
 * sp² centers (same requirement as the bond/angle/torsion packs).
 */
export const buildMmffImpropers = (atoms: Atom[], radialBonds: RadialBondLike[]): ImproperBond[] => {
  const types = perceiveAtomTypes(atoms, radialBonds);
  const neighbors = new Map<Atom, Atom[]>();
  for (const a of atoms) neighbors.set(a, []);
  for (const rb of radialBonds) {
    neighbors.get(rb.atom1)?.push(rb.atom2);
    neighbors.get(rb.atom2)?.push(rb.atom1);
  }
  const impropers: ImproperBond[] = [];
  for (const a of atoms) {
    const t = types.get(a);
    if (t === undefined) continue;
    const ns = neighbors.get(a)!;
    if (ns.length !== 3) continue; // only 3-coordinate sp² centers are planar
    // Center-out-of-plane: keeps the sp² atom coplanar with its 3 neighbors.
    const k = lookupImproperK(t);
    if (k !== null) {
      const ib = new ImproperBond(a, ns[0], ns[1], ns[2]);
      ib.strength = k;
      impropers.push(ib);
    }
    // Hydrogen-out-of-plane (Wilson): for each H on an sp² carbon, keep the H in
    // the plane of the carbon's two other substituents. Governs the strong
    // out-of-plane C–H bend band that the center improper alone places wrong.
    if (t === 'C.2' || t === 'C.ar') {
      for (let hi = 0; hi < ns.length; hi++) {
        if (ns[hi].elementSymbol !== 'H') continue;
        const others = ns.filter((_, i) => i !== hi);
        const ih = new ImproperBond(ns[hi], a, others[0], others[1]);
        ih.strength = HYDROGEN_IMPROPER_K;
        impropers.push(ih);
      }
    }
  }
  return impropers;
};
